import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, Clock, CheckCircle } from 'lucide-react';
import { AuthContext } from '../App';
import CabinCrewLayout from '../layouts/CabinCrewLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReadingTest = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(1200); // 20 minutes
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    startSession();
  }, []);
  
  useEffect(() => {
    if (session && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      handleSubmit();
    }
  }, [timeLeft, session]);
  
  const startSession = async () => {
    try {
      const response = await axios.post(`${API}/reading/start-session`, {}, {
        withCredentials: true
      });
      setSession(response.data);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Önce seviye tespit sınavı yapmalısınız');
        navigate('/dashboard');
      } else {
        toast.error('Oturum başlatılamadı');
      }
    }
  };
  
  const handleAnswer = (questionId, answer) => {
    setAnswers({
      ...answers,
      [questionId]: answer
    });
  };
  
  const handleNext = () => {
    if (currentQuestion < session.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };
  
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  
  const handleSubmit = async () => {
    try {
      const response = await axios.post(
        `${API}/reading/submit`,
        {
          session_id: session.session_id,
          answers: Object.entries(answers).map(([qId, answer]) => ({
            question_id: parseInt(qId),
            answer: answer
          }))
        },
        { withCredentials: true }
      );
      
      toast.success('Reading testi tamamlandı!');
      navigate(`/reading-results/${session.session_id}`);
    } catch (error) {
      toast.error('Test gönderilemedi');
    }
  };
  
  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Test hazırlanıyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }
  
  if (!session) return null;
  
  const question = session.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / session.questions.length) * 100;
  const answeredCount = Object.keys(answers).length;
  
  return (
    <CabinCrewLayout>
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center justify-between mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4" />
              {answeredCount}/{session.questions.length} cevaplandı
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 rounded-full">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-600">
                {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Soru {currentQuestion + 1} / {session.questions.length}</span>
            <span className="text-sm font-semibold text-blue-600">Seviye: {session.level}</span>
          </div>
          <Progress value={progress} />
        </div>
        
        <Card className="glass-effect border-none shadow-xl mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-blue-600" />
              Reading Comprehension
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Passage */}
            {question.passage && (
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-sm font-semibold text-gray-700 mb-3">Metni Okuyun:</p>
                <p className="text-gray-800 leading-relaxed whitespace-pre-line">{question.passage}</p>
              </div>
            )}
            
            {/* Question */}
            <div>
              <p className="text-lg font-semibold text-gray-900 mb-4">{question.question}</p>
              
              {/* Multiple Choice */}
              {question.type === 'multiple_choice' && (
                <div className="space-y-3">
                  {question.options.map((option, idx) => (
                    <label
                      key={idx}
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        answers[currentQuestion] === option
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question-${currentQuestion}`}
                        value={option}
                        checked={answers[currentQuestion] === option}
                        onChange={(e) => handleAnswer(currentQuestion, e.target.value)}
                        className="mr-3"
                      />
                      <span className="text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              )}
              
              {/* Open-ended */}
              {question.type === 'open_ended' && (
                <textarea
                  value={answers[currentQuestion] || ''}
                  onChange={(e) => handleAnswer(currentQuestion, e.target.value)}
                  placeholder="Cevabınızı buraya yazın..."
                  className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none min-h-[150px]"
                />
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            variant="outline"
          >
            Önceki
          </Button>
          
          <div className="flex gap-3">
            {currentQuestion < session.questions.length - 1 ? (
              <Button onClick={handleNext} className="bg-blue-600 hover:bg-blue-700">
                Sonraki
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                className="bg-green-600 hover:bg-green-700"
                disabled={answeredCount < session.questions.length}
              >
                Testi Bitir
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
    </CabinCrewLayout>
  );
};

export default ReadingTest;
